import { useState } from "react";
import adminApi from "@/lib/adminApi";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Save, X, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ImageUploader from "./ImageUploader";


export default function ShopBannersTab({ banners, onRefresh }) {
  const [bannerForm, setBannerForm] = useState({
    image_url: "",
    mobile_image_url: "",
    link_url: "",
    order: 0,
    active: true
  });
  const [editingBanner, setEditingBanner] = useState(null);

  const handleBannerSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...bannerForm,
        mobile_image_url: bannerForm.mobile_image_url || null
      };
      if (editingBanner) {
        await adminApi.put(`/shop-banners/${editingBanner.id}`, payload);
        toast.success("Banner de tienda actualizado");
      } else {
        await adminApi.post(`/shop-banners`, payload);
        toast.success("Banner de tienda creado");
      }
      resetBannerForm();
      onRefresh();
    } catch (error) {
      toast.error("Error al guardar banner");
    }
  };

  const handleEditBanner = (banner) => {
    setEditingBanner(banner);
    setBannerForm({
      image_url: banner.image_url,
      mobile_image_url: banner.mobile_image_url || "",
      link_url: banner.link_url || "",
      order: banner.order,
      active: banner.active
    });
  };

  const handleDeleteBanner = async (bannerId) => {
    if (!window.confirm("¿Eliminar este banner de tienda?")) return;
    try {
      await adminApi.delete(`/shop-banners/${bannerId}`);
      toast.success("Banner de tienda eliminado");
      onRefresh();
    } catch (error) {
      toast.error("Error al eliminar banner");
    }
  };

  const resetBannerForm = () => {
    setEditingBanner(null);
    setBannerForm({ image_url: "", mobile_image_url: "", link_url: "", order: 0, active: true });
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      {/* Banner Form */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm" data-testid="shop-banner-form-card">
        <h2 className="text-xl font-bold mb-6 flex items-center gap-2 text-black">
          {editingBanner ? <Edit2 size={20} /> : <Plus size={20} />}
          {editingBanner ? "Editar Banner de Tienda" : "Nuevo Banner de Tienda"}
        </h2>
        <form onSubmit={handleBannerSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-600 mb-2 font-medium">Imagen Desktop *</label>
            <ImageUploader
              value={bannerForm.image_url}
              onChange={(url) => setBannerForm({...bannerForm, image_url: url})}
              category="banners"
              placeholder="https://... o sube una imagen"
              recommendedSize="Tamaño recomendado: 1920x430 píxeles"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-2 font-medium">Imagen Móvil (opcional)</label>
            <ImageUploader
              value={bannerForm.mobile_image_url}
              onChange={(url) => setBannerForm({...bannerForm, mobile_image_url: url})}
              category="banners"
              placeholder="https://... o sube una imagen"
              recommendedSize="Tamaño recomendado: 768x215 píxeles. Si no se especifica, se usa la imagen desktop."
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-2 font-medium">URL de Destino (opcional)</label>
            <Input
              value={bannerForm.link_url}
              onChange={(e) => setBannerForm({...bannerForm, link_url: e.target.value})}
              placeholder="https://... (producto, colección, etc.)"
              className="bg-gray-50 border-gray-200 text-black focus:border-[#E91E8C]"
              data-testid="shop-banner-link-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-2 font-medium">Orden</label>
              <Input
                type="number"
                value={bannerForm.order}
                onChange={(e) => setBannerForm({...bannerForm, order: parseInt(e.target.value) || 0})}
                className="bg-gray-50 border-gray-200 text-black focus:border-[#E91E8C]"
                data-testid="shop-banner-order-input"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-2 font-medium">Estado</label>
              <Select
                value={bannerForm.active ? "active" : "inactive"}
                onValueChange={(value) => setBannerForm({...bannerForm, active: value === "active"})}
              >
                <SelectTrigger className="bg-gray-50 border-gray-200 text-black" data-testid="shop-banner-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Activo</SelectItem>
                  <SelectItem value="inactive">Inactivo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-3 pt-4">
            <Button type="submit" className="bg-black hover:bg-gray-800 text-white rounded-full" data-testid="shop-banner-submit-btn">
              <Save size={16} className="mr-2" />
              {editingBanner ? "Actualizar" : "Crear"}
            </Button>
            {editingBanner && (
              <Button type="button" variant="outline" onClick={resetBannerForm} className="rounded-full" data-testid="shop-banner-cancel-btn">
                <X size={16} className="mr-2" />
                Cancelar
              </Button>
            )}
          </div>
        </form>
      </div>

      {/* Banners List */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm" data-testid="shop-banners-list">
        <h2 className="text-xl font-bold mb-6 text-black flex items-center gap-2">
          <ShoppingBag size={20} className="text-[#E91E8C]" />
          Banners de la Tienda
        </h2>
        {banners.length === 0 ? (
          <p className="text-gray-500">No hay banners de tienda. Crea uno para comenzar.</p>
        ) : (
          <div className="space-y-3 max-h-[600px] overflow-y-auto">
            {banners.map((banner) => (
              <div 
                key={banner.id}
                className={`flex items-center justify-between p-4 rounded-xl border ${banner.active ? 'bg-gray-50 border-gray-100' : 'bg-gray-100 border-gray-200 opacity-60'}`}
                data-testid={`shop-banner-item-${banner.id}`}
              >
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className="flex gap-2 flex-shrink-0">
                    <img 
                      src={banner.image_url} 
                      alt="Desktop"
                      className="w-24 h-12 object-cover rounded-lg"
                      title="Desktop"
                    />
                    {banner.mobile_image_url && (
                      <img 
                        src={banner.mobile_image_url} 
                        alt="Móvil"
                        className="w-12 h-12 object-cover rounded-lg border-2 border-blue-400"
                        title="Móvil"
                      />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-black text-sm">
                      Orden: {banner.order}
                      {!banner.active && <span className="ml-2 text-xs text-gray-400">(Inactivo)</span>}
                    </p>
                    <p className="text-xs text-gray-500">
                      {banner.mobile_image_url ? '📱 Con imagen móvil' : '🖥️ Solo desktop'}
                    </p>
                    {banner.link_url && (
                      <p className="text-xs text-gray-400 truncate max-w-[200px]">
                        {banner.link_url}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0 ml-4">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleEditBanner(banner)}
                    className="text-gray-600 hover:text-black"
                    data-testid={`edit-shop-banner-${banner.id}`}
                  >
                    <Edit2 size={16} />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    onClick={() => handleDeleteBanner(banner.id)}
                    data-testid={`delete-shop-banner-${banner.id}`}
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
